import * as stylex from '@stylexjs/stylex';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectItemSearchModal } from '@/core/SelectItemSearchModal.molecule';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { moveDisplay } from '@/displays/displays.actions';
import { DISPLAYS_CATEGORY } from '@/displays/displays.constants';
import { computeMoveDisplayAsset } from '@/displays/displays.utilities';
import { SEARCH_TYPES } from '@/main/app.constants';
import { exploreAssetSearchActions } from '@/search/search.actions';
import { useSearchMode } from '@/search/search.store';
import { doTrack } from '@/track/track.service';
import { errorToast } from '@/utilities/toast.utilities';
import { SeeqNames } from '@seeqdev/constants';
import { Alert, Button } from '@seeqdev/qomponents';

interface MoveDisplayModalProps {
  display: { id: string; name: string; ancestors: { id: string }[] };
  onClose: () => void;
}

export const MoveDisplayModal = (props: MoveDisplayModalProps) => {
  const { display, onClose } = props;
  const { t } = useTranslation();
  const [assetId, setAssetId] = useState<string>();
  const [isInitialized, setIsInitialized] = useState(false);
  const stateParams = useFluxPath(sqWorkbenchStore, (store) => store.stateParams);
  const mode = useSearchMode();

  useEffect(() => {
    const initialAssetId = computeMoveDisplayAsset(display);
    exploreAssetSearchActions('modal', initialAssetId!, SEARCH_TYPES, false, [stateParams.workbookId], true)
      .then(() => {
        setIsInitialized(true);
      })
      .catch((error) => errorToast({ httpResponseOrError: error }));
  }, []);

  const isValidAsset = (item: any): Promise<boolean> => {
    const isScopedToWorkbook = item.scopedTo === stateParams.workbookId;
    const isWorkbookScopedSearch = mode === 'search';
    const isNotCurrentParentAsset = item.id !== display?.ancestors?.slice(-1)?.[0]?.id;
    const isValid = (isScopedToWorkbook || isWorkbookScopedSearch) && isNotCurrentParentAsset;
    return Promise.resolve(isValid);
  };

  const updateDisplayParent = (parentId: string) => {
    onClose();
    return moveDisplay(display.id, parentId);
  };

  const cancelMoveDisplay = () => {
    doTrack(DISPLAYS_CATEGORY, 'move', 'cancel');
    onClose();
  };

  const footer = (
    <div {...stylex.props(styles.footer)}>
      <Button
        onClick={cancelMoveDisplay}
        label={t('DISPLAYS.MODAL.MOVE.CANCEL')}
        xstyle={styles.cancel}
        testId="moveDisplayCancelButton"
      />
      <Button
        onClick={() => {
          updateDisplayParent(assetId!);
        }}
        disabled={!assetId}
        label={t('DISPLAYS.MODAL.MOVE.MOVE')}
        variant="theme"
        testId="moveDisplayMoveButton"
      />
    </div>
  );

  const title = t('DISPLAYS.MODAL.MOVE.HEADER');

  const body = (
    <Alert variant="theme" dismissible={false} testId="moveDisplayHelpText">
      {t('DISPLAYS.MODAL.MOVE.HELP_TEXT', { DISPLAY_NAME: display?.name })}
    </Alert>
  );

  return isInitialized ? (
    <SelectItemSearchModal
      modalId="moveDisplayModal"
      onClose={cancelMoveDisplay}
      iconPartialTooltipKey="DISPLAYS.MODAL.MOVE.TOOLTIPS"
      onSelect={(item) => {
        setAssetId(item.id);
      }}
      validateItem={isValidAsset}
      selectItems={assetId ? [{ id: assetId }] : []}
      scopeIds={[stateParams.workbookId]}
      excludeGloballyScoped={true}
      body={body}
      header={{ title }}
      footer={footer}
      testId="moveDisplayModal"
      searchTypes={[SeeqNames.Types.Asset]}
    />
  ) : null;
};

const styles = stylex.create({
  footer: { display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', padding: '0.625rem' },
  cancel: { marginRight: 20 },
});
